import React, { FC, useState, useCallback, useEffect, useMemo } from "react";
import debounce from "lodash/debounce";
import classnames from "classnames";
import { AutoComplete as AntdAutoComplete } from "antd";
import { FieldError, FieldErrorsImpl, Merge } from "react-hook-form";

import { CloseIco, DropDownIco } from "../../assets/customIcons";
import { FormFieldWrapper } from "../index";
import addressApi from "../../api/Address";

import styles from "./AutoComplete.module.scss";

interface IAutoComplete {
  field?: any;
  label: string;
  allowClear?: boolean;
  required?: boolean;
  isRequired?: boolean;
  disabled?: boolean;
  error?: FieldError | string | Merge<FieldError, FieldErrorsImpl<any>>;
  onChange?: (value: string | unknown, obj?: unknown) => void;
  mapOptions?: (options: { [key: string]: any }[]) => { [key: string]: any }[];
  onSelect?: (option: string) => void;
  getData?: (page: number, count: number, value: string) => void;
  info?: string | JSX.Element;
  placement?: "top" | "bottom" | "left" | "right";
  autocomplete?: string;
  defaultSearchString?: string;
}

export const AutoComplete: FC<IAutoComplete> = ({
  field,
  error,
  label,
  info,
  placement,
  allowClear = true,
  required = false,
  isRequired = false,
  disabled = false,
  mapOptions,
  onChange,
  onSelect,
  getData,
  autocomplete = "off",
  defaultSearchString = "Санкт",
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [selectableValue, setSelectableValue] = useState(null);

  useEffect(() => {
    if (!isFocused && !selectableValue) {
      setIsOpen(false);
    } else if (isFocused && !selectableValue && defaultSearchString!) {
      onHandleSearch(defaultSearchString);
    }
  }, [isFocused, selectableValue]);

  const getOptions = async (value) => {
    if (typeof getData === "function") return getData(1, 30, value);
    else return addressApi.getAddressList(value, 30);
  };

  const onHandleSelect = (value) => {
    const selected = options.find((option) => option.value === value);
    if (typeof onSelect === "function") {
      onSelect(selected);
    }
  };

  const onHandleSearch = async (value) => {
    const res: any = await getOptions(value);
    let options = res?.data?.items;

    if (mapOptions) {
      options = mapOptions(options);
    }

    options = options.map((item) => {
      return { ...item, value: item.label };
    });

    setOptions(options);
  };

  const debounceQuery = useCallback(debounce(onHandleSearch, 400), []);

  const onDropdownVisibleChange = (open) => {
    if (selectableValue) {
      setIsOpen(open);
    }
  };

  const getName = useMemo(() => {
    const { name = "" } = field || { name: "" };
    if (field?.name && (isFocused || isOpen) && autocomplete !== "on") {
      return "";
    }
    return name;
  }, [field, isFocused, isOpen]);

  return (
    <div className={styles.fieldWrapper}>
      <FormFieldWrapper
        error={error}
        label={label}
        info={info}
        placement={placement}
        value={field?.value}
        required={required || isRequired}
        disabled={disabled}
        isFocused={isFocused || field?.value}
        className={classnames(styles.field, { [styles.open]: isOpen })}
        labelClassName={styles.label}
      >
        <>
          <AntdAutoComplete
            {...field}
            name={getName}
            options={options}
            onSearch={debounceQuery}
            onSelect={onHandleSelect}
            onChange={(value, obj) => {
              if (value === "" || value === undefined) {
                setSelectableValue(null);
              } else {
                setSelectableValue(value);
              }
              field.onChange(value);
              if (typeof onChange === "function") {
                onChange(value, obj);
              }
              setIsFocused(false);
            }}
            onFocus={(e) => {
              setIsFocused(true);
            }}
            onBlur={(e) => {
              field.onBlur();
              setIsFocused(false);
            }}
            onDropdownVisibleChange={onDropdownVisibleChange}
            allowClear={allowClear}
            clearIcon={<CloseIco />}
            className={styles.field}
            status={error ? "error" : null}
            disabled={disabled}
          />
          <div
            className={classnames(styles.arrow, {
              [styles.arrowDisabled]: disabled,
            })}
          >
            <DropDownIco />
          </div>
        </>
      </FormFieldWrapper>
    </div>
  );
};
